import fetchMultipart from 'fetch-multipart-graphql'
import { hydrateRelayEnvironment } from 'relay-nextjs'
import createRelaySubscriptionHandler from 'graphql-ruby-client/subscriptions/createRelaySubscriptionHandler'
import {
  Environment,
  Network,
  Observable,
  RecordSource,
  RequestParameters,
  Store,
  SubscribeFunction,
  Variables,
} from 'relay-runtime'
import { shouldTrackError, trackError } from 'services/analytics'
import { isPrerendering } from 'services/prerender'
import { getErrorSeverity } from 'services/error'
import {
  errorMiddleware,
  loggerMiddleware,
  perfMiddleware,
  RelayNetworkLayer,
  retryMiddleware,
  urlMiddleware,
} from 'react-relay-network-modern'
import { isDevelopment } from 'services/environment'
import { getGraphQLBaseURL } from 'api/url'
import { missingFieldHandlers } from './missingFieldsHandlers'
import { createCookieAwareRequestHandler } from './requestHandler'
import { graphQLErrorExtraData, hasGraphQLErrors } from '../error'

export function getWebsocketBaseURL() {
  return `${getGraphQLBaseURL().replace('/api/v2', '')}/websocket`
}

export function getActionCableConsumer() {
  if (typeof window !== 'undefined' && (window as any).actionCableConsumer) {
    return (window as any).actionCableConsumer
  }

  // NOTE: do not move actioncable import to an "import" statement:
  // that will make Next.js crash server side.
  // See: https://github.com/rails/rails/pull/39543
  // eslint-disable-next-line global-require
  const ActionCable = require('@rails/actioncable')
  const consumer = ActionCable.createConsumer(getWebsocketBaseURL())
  // Add a global way to access the actionCableConsumer. This is needed for example by the method that is able to
  // kill and re-connect all channels in case the user masks into another user
  const w = (window as any)
  w.actionCableConsumer = consumer

  return consumer
}

function createSubscriptionHandler(): SubscribeFunction {
  return createRelaySubscriptionHandler({
    cable: getActionCableConsumer(),
  })
}

/**
 * This is a fetch function suitable for Relay's Network that can stream multi part responses.
 * Useful for stream and defer directives.
 *
 * NOTE: causes issues with logrocket and other tools, they can't see the requests in flight.
 *
 * @param params fetch request parameters
 * @param variables query variables
 * @returns Observable
 */
function streamEnabledClientFetchFunction(params: RequestParameters, variables: Variables) {
  return Observable.create((sink) => {
    fetchMultipart(getGraphQLBaseURL(), {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
      },
      body: JSON.stringify({
        query: params.text,
        variables,
      }),
      credentials: 'include', // https://github.com/developit/unfetch#fetchurl-string-options-object
      onNext: (parts: unknown[]) => {
        const doErrorDetection = Boolean(parts?.length > 0 && parts?.[0])

        if (doErrorDetection) {
          for (const part of parts) {
            if (hasGraphQLErrors(part)) {
              const graphQLError = part.errors[0]
              if (graphQLError && shouldTrackError(graphQLError)) {
                const extraData = graphQLErrorExtraData({
                  variables,
                  requestParameters: params,
                  response: part,
                })
                trackError(graphQLError, getErrorSeverity(graphQLError), extraData)
              }
            }
          }
        }

        // NOTE: after the following call, the "errors" array is removed
        // This is why we have to report the error now
        sink.next(parts)
      },
      onError: (err: unknown) => {
        sink.error(err as Error)
      },
      onComplete: () => {
        sink.complete()
      },
    })
    // TODO: figure out how automatically call trackError using methods in api/graphql/index.ts
    // when an 'errors' array is returned with values from the backend
  })
}

/**
 * This creates a relay network for the browser that will use our optimized request handler and subscription handler
 * @returns a Relay Network
 */
export function createClientNetwork() {
  return Network.create(createCookieAwareRequestHandler(), createSubscriptionHandler())
}

/**
 * This creates a relay network for the browser that leverages middlewares to do exponential backoff retries and more
 *
 * @returns a Relay Network
 */
export function createEnhancedClientNetwork() {
  // Note: this come from the example here https://github.com/relay-tools/react-relay-network-modern?tab=readme-ov-file#example-of-injecting-networklayer-with-middlewares-on-the-client-side
  return new RelayNetworkLayer([
    urlMiddleware({
      url: _req => Promise.resolve(getGraphQLBaseURL()),
      credentials: 'include',
    }),
    isDevelopment() ? loggerMiddleware() : null,
    isDevelopment() ? errorMiddleware() : null,
    isDevelopment() ? perfMiddleware() : null,
    retryMiddleware({
      fetchTimeout: 15000,
      retryDelays: attempt => Math.pow(2, attempt + 4) * 100, // or simple array [3200, 6400, 12800, 25600, 51200, 102400, 204800, 409600],
      beforeRetry: ({ forceRetry, abort, delay, attempt, lastError }) => {
        const extraInfo = {
          attempt,
          delay,
        }
        if (attempt > 10) {
          abort()
          if (lastError) trackError(lastError, 'high', extraInfo)
        } else {
          if (lastError) trackError(lastError, 'low', extraInfo)
        }
        const w = window as any
        w.forceRelayRetry = forceRetry
        console.log(`call \`forceRelayRetry()\` for immediately retry! Or wait ${delay} ms.`)
      },
      statusCodes: [408, 500, 503, 504],
    }),
  ], {
    subscribeFn: createSubscriptionHandler() as any, // TODO: fix type cast here
  })
}

export function getClientEnvironment(): Environment | null {
  if (isPrerendering()) return null

  const w = window as any
  if (w.relayEnvironment) return w.relayEnvironment as Environment

  const recordSource = new RecordSource()

  w.relayEnvironment = new Environment({
    network: createClientNetwork(),
    store: new Store(recordSource, {
      gcReleaseBufferSize: 50, // Unneeded query cache to keep in memory
      queryCacheExpirationTime: 24 * 60 * 60 * 1000, // Expiration time in milliseconds for query cache
    }),
    missingFieldHandlers,
  })

  // Load SSR data into the environment
  try {
    hydrateRelayEnvironment(w.relayEnvironment)
  } catch (error) {
    trackError(error as Error, 'low')
  }

  return w.relayEnvironment as Environment
}
